import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollText, Shield, Check, X, Edit, Flag } from 'lucide-react';

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');

  useEffect(() => {
    const params = {};
    if (filterAction && filterAction !== 'all') params.action = filterAction;
    api.get('/audit-logs', { params }).then(r => setLogs(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [filterAction]);

  const actionIcon = (action) => {
    if (action === 'approve' || action === 'bulk_approve') return <Check className="h-3.5 w-3.5 text-emerald-600" />;
    if (action === 'reject') return <X className="h-3.5 w-3.5 text-red-500" />;
    if (action === 'override_attendance') return <Shield className="h-3.5 w-3.5 text-amber-500" />;
    if (action === 'flag_audit') return <Flag className="h-3.5 w-3.5 text-purple-500" />;
    return <Edit className="h-3.5 w-3.5 text-neutral-500" />;
  };

  const actionColor = (action) => {
    if (action === 'approve' || action === 'bulk_approve') return 'bg-emerald-100 text-emerald-700';
    if (action === 'reject') return 'bg-red-100 text-red-700';
    if (action === 'override_attendance') return 'bg-amber-100 text-amber-700';
    if (action === 'flag_audit') return 'bg-purple-100 text-purple-700';
    return 'bg-neutral-100 text-neutral-700';
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Audit Log</h1>
          <p className="text-neutral-500 text-sm mt-1">Track all system actions and approvals</p>
        </div>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="audit-filter-action">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="approve">Approve</SelectItem>
            <SelectItem value="bulk_approve">Bulk Approve</SelectItem>
            <SelectItem value="reject">Reject</SelectItem>
            <SelectItem value="override_attendance">Override</SelectItem>
            <SelectItem value="flag_audit">Audit Flag</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Time</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">User</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Action</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Entity</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={5}><div className="h-8 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-neutral-500">
                  <ScrollText className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No audit logs yet
                </TableCell></TableRow>
              ) : logs.map(log => (
                <TableRow key={log.id} className="hover:bg-neutral-50" data-testid={`audit-row-${log.id}`}>
                  <TableCell className="text-xs text-neutral-600">{formatDate(log.created_at)}</TableCell>
                  <TableCell className="text-sm font-medium text-neutral-800">{log.user_name}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs capitalize ${actionColor(log.action)} flex items-center gap-1 w-fit`}>
                      {actionIcon(log.action)} {log.action.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-neutral-600">{log.entity_type}</TableCell>
                  <TableCell className="text-xs text-neutral-500 max-w-[300px] truncate hidden sm:table-cell">{log.details}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
